<template>
  <teleport to="body">
    <div v-if="open" class="fixed inset-0 z-50 overflow-y-auto">
      <!-- Backdrop -->
      <div class="fixed inset-0 bg-black/50 transition-opacity" @click="$emit('close')"></div>

      <!-- Modal -->
      <div class="flex items-end sm:items-start justify-center min-h-screen sm:px-4 sm:py-8">
        <div
          class="relative bg-white dark:bg-gray-800 rounded-none sm:rounded-lg text-left overflow-hidden shadow-xl dark:shadow-gray-900/50 transform transition-all w-full h-[100dvh] sm:h-auto sm:max-w-[1200px] sm:max-h-[90vh] flex flex-col"
          @click.stop
        >
          <!-- Header -->
          <div
            class="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 px-6 py-4 flex items-center justify-between"
          >
            <div class="min-w-0 flex-1">
              <h2 class="text-white text-lg font-semibold truncate" :title="prospect?.subject">
                {{ prospect?.subject }}
              </h2>
              <p class="text-blue-100 text-sm truncate">
                {{ prospect?.organization_name || 'Geen organisatie' }}
              </p>
              <p class="text-blue-200 text-sm truncate">
                {{ prospect?.responsible_employee_name || 'Geen verantwoordelijke' }}
              </p>
            </div>
            <button
              @click="$emit('close')"
              class="ml-4 text-white/80 hover:text-white flex-shrink-0"
            >
              <XMarkIcon class="h-6 w-6" />
            </button>
          </div>

          <!-- Body - scrollable -->
          <div class="flex-1 overflow-y-auto px-3 sm:px-6 py-3 sm:py-4">
            <!-- Project Info -->
            <ProjectInfoCard
              v-if="prospect"
              ref="projectInfoCardRef"
              :prospect="prospect"
              :workflow-status="workflowStatus"
              :onUpdateDates="handleUpdateDates"
              :onUpdateChanceToScore="handleUpdateChanceToScore"
              :teams="teams"
              :responsible-team-id="responsibleTeamId"
              :can-edit-team="canEditTeam"
              :on-update-team="onUpdateTeam"
              :team-error="teamError"
              class="mb-6"
            />

            <!-- Main content slot -->
            <slot></slot>
          </div>

          <!-- Footer slot -->
          <div
            v-if="$slots.footer"
            class="bg-gray-50 dark:bg-gray-700 px-3 sm:px-6 py-3 sm:py-4 border-t border-gray-200 dark:border-gray-600"
          >
            <slot name="footer"></slot>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import ProjectInfoCard from '../../project-detail/ProjectInfoCard.vue'
import { useScrollLock } from '../../../composables/ui/useScrollLock'
import { prospectsLeadsService } from '../../../services/prospectService.js'

const props = defineProps({
  open: {
    type: Boolean,
    default: false
  },
  prospect: {
    type: Object,
    default: null
  },
  workflowStatus: {
    type: Object,
    default: null
  },
  teams: {
    type: Array,
    default: null
  },
  responsibleTeamId: {
    type: String,
    default: null
  },
  canEditTeam: {
    type: Boolean,
    default: false
  },
  onUpdateTeam: {
    type: Function,
    default: null
  },
  teamError: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'prospect-updated'])

// Ref for ProjectInfoCard
const projectInfoCardRef = ref(null)

// Handle date updates
const handleUpdateDates = async ({ start_date, expected_closing_date }) => {
  projectInfoCardRef.value?.setLoading(true)
  projectInfoCardRef.value?.setError('')
  projectInfoCardRef.value?.setSyncStatus('Opslaan...')

  try {
    // Only send the date that was actually changed
    const updateData = {}
    if (start_date) updateData.start_date = start_date
    if (expected_closing_date) updateData.expected_closing_date = expected_closing_date

    const result = await prospectsLeadsService.updateDates(props.prospect.id, updateData)

    if (result.success) {
      // Emit event to update parent's prospect data
      emit('prospect-updated', result.data)

      // Show sync status
      if (result.simplicate_synced) {
        projectInfoCardRef.value?.setSyncStatus('Gesynchroniseerd met Simplicate')
      } else if (result.simplicate_skipped) {
        projectInfoCardRef.value?.setSyncStatus(
          'Lokaal opgeslagen (Simplicate sync niet actief voor dit project)'
        )
      } else if (result.simplicate_error) {
        projectInfoCardRef.value?.setSyncStatus(
          `Lokaal opgeslagen, Simplicate sync mislukt: ${result.simplicate_error}`
        )
      } else {
        projectInfoCardRef.value?.setSyncStatus('Opgeslagen')
      }

      projectInfoCardRef.value?.closeEditing()

      // Clear status after 5 seconds
      setTimeout(() => projectInfoCardRef.value?.setSyncStatus(''), 5000)
    } else {
      projectInfoCardRef.value?.setError(result.error || 'Fout bij opslaan')
    }
  } catch (err) {
    console.error('Error updating dates:', err)
    projectInfoCardRef.value?.setError(err.response?.data?.error || 'Fout bij opslaan van datums')
  } finally {
    projectInfoCardRef.value?.setLoading(false)
  }
}

// Handle chance_to_score updates
const handleUpdateChanceToScore = async ({ chance_to_score }) => {
  projectInfoCardRef.value?.setLoading(true)
  projectInfoCardRef.value?.setError('')
  projectInfoCardRef.value?.setSyncStatus('Opslaan...')

  try {
    const result = await prospectsLeadsService.updateChanceToScore(
      props.prospect.id,
      chance_to_score
    )

    if (result.success) {
      emit('prospect-updated', result.data)

      if (result.simplicate_synced) {
        projectInfoCardRef.value?.setSyncStatus('Gesynchroniseerd met Simplicate')
      } else if (result.simplicate_skipped) {
        projectInfoCardRef.value?.setSyncStatus(
          'Lokaal opgeslagen (Simplicate sync niet actief voor dit project)'
        )
      } else if (result.simplicate_error) {
        projectInfoCardRef.value?.setSyncStatus(
          `Lokaal opgeslagen, Simplicate sync mislukt: ${result.simplicate_error}`
        )
      } else {
        projectInfoCardRef.value?.setSyncStatus('Opgeslagen')
      }

      projectInfoCardRef.value?.closeEditing()
      setTimeout(() => projectInfoCardRef.value?.setSyncStatus(''), 5000)
    } else {
      projectInfoCardRef.value?.setError(result.error || 'Fout bij opslaan')
    }
  } catch (err) {
    console.error('Error updating chance_to_score:', err)
    projectInfoCardRef.value?.setError(
      err.response?.data?.error || 'Fout bij opslaan van scoringskans'
    )
  } finally {
    projectInfoCardRef.value?.setLoading(false)
  }
}

// Scroll lock when modal opens/closes
const { lockScroll, unlockScroll } = useScrollLock()

// Handle Escape key
const handleKeydown = e => {
  if (e.key === 'Escape' && props.open) {
    emit('close')
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})

watch(
  () => props.open,
  newVal => {
    if (newVal) {
      lockScroll()
    } else {
      unlockScroll()
    }
  }
)
</script>
